// Shared static content for ABAY360 marketing/public pages.
// Kept separate so pages stay declarative and easy to update. Additive only.

export const COMPANY = {
  name: "ABAY360",
  tagline: "Ethiopia's smartest AI-powered property platform.",
  founded: "2024",
  story:
    "ABAY360 was born from a simple frustration: finding, buying, renting or investing in property across Ethiopia was slow, opaque and full of uncertainty. Listings were scattered, prices were guesswork, and trust was hard to come by. We set out to change that \u2014 building a single, intelligent platform where every listing is verified, every price is backed by data, and every decision is guided by AI. Today, ABAY360 brings the confidence of world-class PropTech to the Ethiopian market, from Addis Ababa to Hawassa, Adama, Bahir Dar and beyond.",
  mission:
    "To make property decisions in Ethiopia simple, transparent and trustworthy \u2014 empowering every buyer, renter, seller and investor with verified listings and AI-driven insight.",
  vision:
    "To become Ethiopia's most trusted real-estate platform, and the intelligent backbone of property transactions across East Africa.",
};

// ── Single source of truth for all official ABAY360 contact details. ──
// Update here and it propagates to the Contact page, Footer, About, Help,
// FAQ, legal docs and (future) automated emails.
export const CONTACT = {
  email: "info@abay360.com",
  emailHref: "mailto:info@abay360.com",
  // Official ABAY360 business line (currently routed via Google Voice).
  phone: "+1 (929) 399-9398",
  phoneHref: "tel:+19293999398",
  phoneNote: "Official ABAY360 business line",
  telegram: "@abay360",
  telegramHref: "https://t.me/abay360",
  address: "Bole, Addis Ababa, Ethiopia",
  hours: "Mon \u2013 Sat, 8:30 AM \u2013 6:00 PM (EAT)",
  // Bole, Addis Ababa
  map: { lat: 8.9945, lng: 38.7896, zoom: 13 },
};

// Messaging channels — extensible by design. Add new channels (WhatsApp,
// Facebook Messenger, Live Chat, etc.) here with `enabled: true` and a `href`;
// the Contact page and Footer render enabled channels automatically, so no
// layout/redesign is ever required.
export const CONTACT_CHANNELS = [
  { key: "telegram", label: "Telegram", icon: "Send", href: CONTACT.telegramHref, enabled: true },
  { key: "whatsapp", label: "WhatsApp", icon: "MessageCircle", href: "", enabled: false },
  { key: "messenger", label: "Messenger", icon: "MessageSquare", href: "", enabled: false },
  { key: "livechat", label: "Live Chat", icon: "Headphones", href: "", enabled: false },
];

export const enabledChannels = () => CONTACT_CHANNELS.filter((c) => c.enabled && c.href);

export const SOCIALS = [
  { label: "Facebook", icon: "Facebook", href: "https://facebook.com" },
  { label: "Instagram", icon: "Instagram", href: "https://instagram.com" },
  { label: "Twitter", icon: "Twitter", href: "https://twitter.com" },
  { label: "LinkedIn", icon: "Linkedin", href: "https://linkedin.com" },
];

export const CORE_VALUES = [
  { icon: "ShieldCheck", title: "Trust & Transparency", desc: "Every listing is verified and every price is backed by data. No surprises, no hidden agendas." },
  { icon: "Sparkles", title: "Intelligence First", desc: "We put AI to work for you \u2014 smarter search, fair valuations and insights that guide better decisions." },
  { icon: "Users", title: "People at the Center", desc: "Buyers, renters, sellers and agents all deserve a platform that respects their time and goals." },
  { icon: "HeartHandshake", title: "Local Expertise", desc: "Built for Ethiopia, by people who understand its cities, neighborhoods and communities." },
  { icon: "Rocket", title: "Relentless Innovation", desc: "We ship fast and improve constantly, bringing world-class PropTech to the Ethiopian market." },
  { icon: "Lock", title: "Security by Design", desc: "Bank-grade practices and fraud detection protect your data and your transactions." },
];

export const STATS = [
  { value: "12,000+", label: "Verified Listings" },
  { value: "8,500+", label: "Happy Users" },
  { value: "350+", label: "Trusted Agents" },
  { value: "15", label: "Cities Covered" },
];

export const TEAM = [
  { name: "Team Member", role: "Founder & CEO", initials: "AB" },
  { name: "Team Member", role: "Chief Technology Officer", initials: "CT" },
  { name: "Team Member", role: "Head of Product", initials: "HP" },
  { name: "Team Member", role: "Head of Partnerships", initials: "HP" },
];

// Categorized FAQs. Each category renders as a tab with an accordion.
export const FAQ_CATEGORIES = [
  {
    key: "buyers",
    label: "Buyers",
    icon: "Home",
    items: [
      { q: "How do I search for a property on ABAY360?", a: "You can browse by Buy, Rent, Land or Commercial, or simply describe your ideal home in plain language using Abay AI search \u2014 e.g. \u201cModern 3-bedroom villa in Bole under ETB 25M\u201d. Our AI translates that into filters and matches verified listings instantly." },
      { q: "Are the listings verified?", a: "Yes. Every listing is reviewed by our team and trusted agents before it goes live, and our AI runs automated fraud checks to flag suspicious listings." },
      { q: "Can I estimate mortgage payments?", a: "Absolutely. Ask Abay AI for a mortgage estimate and it will calculate approximate monthly payments in ETB based on price, down payment and term. This is an estimate \u2014 always confirm final terms with your lender." },
      { q: "How do I know if a property is fairly priced?", a: "Open any property and check the AI Valuation and Insights sections, or ask Abay AI \u201cIs this fairly priced?\u201d. We analyze price per m\u00b2, location and comparable listings to give you a confident view." },
      { q: "Is there any cost to search or contact agents?", a: "Browsing, searching and contacting listing agents through ABAY360 is free for buyers and renters." },
    ],
  },
  {
    key: "sellers",
    label: "Sellers",
    icon: "Tag",
    items: [
      { q: "How do I list my property?", a: "Create an account, then click \u201cList Property\u201d and follow the guided wizard \u2014 add details, photos, location and pricing, then publish. Your listing is reviewed before going live." },
      { q: "How long does it take for my listing to appear?", a: "Most listings are reviewed and published within 24\u201348 hours. Verified agents may see faster turnaround." },
      { q: "Can AI help me price my property?", a: "Yes. During listing you can use AI Valuation to get a suggested price range with a confidence score, so you can price competitively." },
      { q: "Can I edit or remove my listing later?", a: "Yes. You can manage, edit, unpublish or delete your listings anytime from your Dashboard." },
      { q: "How do buyers contact me?", a: "Interested buyers reach out through the platform via your listing. You control your contact preferences from your profile." },
    ],
  },
  {
    key: "agents",
    label: "Agents",
    icon: "UserCheck",
    items: [
      { q: "How do I become a verified agent?", a: "Sign up and select the Agent role. Complete your profile and submit verification details \u2014 once approved, you get a verified badge and enhanced listing tools." },
      { q: "What tools do agents get?", a: "Verified agents get AI-assisted listing creation, valuation tools, listing analytics, and priority placement, plus a professional public profile." },
      { q: "Can I manage multiple listings?", a: "Yes. Your Dashboard lets you manage all your listings, favorites and saved searches in one place." },
      { q: "Is there a fee for agents?", a: "Core listing tools are free during launch. Premium agent subscriptions with advanced analytics and promotion are coming in a future phase." },
    ],
  },
  {
    key: "investors",
    label: "Investors",
    icon: "TrendingUp",
    items: [
      { q: "Can ABAY360 help me find high-ROI properties?", a: "Yes. Ask Abay AI for investment advice or check the Insights and Valuation sections, which include estimated rental yield and ROI potential for listings." },
      { q: "Which areas show strong investment potential?", a: "Trends shift over time, but areas like Bole and Hawassa have historically shown strong demand. Use AI Insights on individual properties for the latest data-driven view." },
      { q: "How is rental yield estimated?", a: "We estimate gross yield as annual rent divided by price. Net yield depends on your costs (maintenance, management, taxes) \u2014 always run your own due diligence." },
      { q: "Does ABAY360 provide market insights?", a: "Yes. Our AI surfaces neighborhood highlights, price context and investment potential. Deeper market analytics dashboards are on our roadmap." },
    ],
  },
];
